import { useId, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import Modal from '../Modal';
import SearchableSelect, { type SearchableOption } from '../SearchableSelect';
import MultiSearchableSelect from '../MultiSearchableSelect';
import type {
  ConditionOp,
  PlanCondition,
  PlanFieldInfo,
  PlanFieldKey,
  PlanFieldsResponse,
  PlanPriorityConfig,
  PlanVisits,
} from '../../types/preJourneyPlan';
import { MAX_CONDITIONS, PRIORITY_NAME_MAX, fieldMapOf, fold, valueLabel } from './preJourneyPlanConfig';

/**
 * Store Visits Configuration › Pre-Journey Plan › Add / Edit priority.
 *
 * A priority is a name, one to five conditions that must all match (on the store, or on any
 * person deployed at it) and the visits a matching store gets: a count for each month of the
 * quarter, or one count for the quarter. Done hands the priority back to the tab; nothing is
 * saved until the tab's Save.
 */

export type PriorityDraftResult = Pick<PlanPriorityConfig, 'name' | 'conditions' | 'visits'>;

interface Props {
  /** The priority being edited; left out, a new one is added. */
  initial?: PlanPriorityConfig;
  fields: PlanFieldsResponse;
  onClose: () => void;
  onDone: (priority: PriorityDraftResult) => void;
}

const OP_LABEL: Record<ConditionOp, string> = {
  any_of: 'is',
  none_of: 'is not',
  at_least: 'at least',
  at_most: 'at most',
  within_months: 'within',
  months_running: 'months running',
  is_true: 'is yes',
  is_false: 'is no',
};
const VALUE_OPS: ConditionOp[] = ['any_of', 'none_of'];
const NUMBER_OPS: ConditionOp[] = ['at_least', 'at_most', 'within_months', 'months_running'];
/** Whole-number limits of the people fields; a number custom field takes any number. */
const RANGE: Partial<Record<string, [number, number]>> = { joined_within: [1, 60], red_flags: [1, 99], score_fell: [1, 6] };
const START_NUMBER: Partial<Record<string, string>> = { joined_within: '12', red_flags: '1', score_fell: '2' };

const VISITS_MAX = 10;
const MONTH_ERROR = `Enter 0 to ${VISITS_MAX} for each month, at least one above 0.`;
const QUARTER_ERROR = `Enter a whole number from 1 to ${VISITS_MAX}.`;

interface DraftCondition {
  field: PlanFieldKey | '';
  op: ConditionOp | '';
  /** The values picked, for a field that lists its values. */
  values: string[];
  /** The values as typed, for a field that lists none (an employee text field): read on blur and on Done. */
  text: string;
  number: string;
}
type Mode = PlanVisits['mode'];

const EMPTY_CONDITION: DraftCondition = { field: '', op: '', values: [], text: '', number: '' };

/** 'Senior iPro, Store Lead,, senior ipro' → ['Senior iPro', 'Store Lead']: comma between, trimmed, no blanks or repeats. */
const typedValues = (text: string): string[] => {
  const seen = new Set<string>();
  return text.split(',').map((v) => v.trim()).filter((v) => {
    if (!v || seen.has(fold(v))) return false;
    seen.add(fold(v));
    return true;
  });
};

const wholeNumber = (text: string): number | null => (/^\d+$/.test(text.trim()) ? Number(text.trim()) : null);

const FIELD = 'rounded-lg border px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50';
const TEXT = 'w-full rounded-lg border px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';
const ADD_LINK = 'text-sm font-medium text-primary-600 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-50';
const ERROR = 'mt-1 text-xs text-red-700';

export default function PreJourneyPriorityModal({ initial, fields, onClose, onDone }: Props) {
  const id = useId();
  const byKey = useMemo(() => fieldMapOf([fields.store, fields.people]), [fields]);
  const fieldOptions = useMemo<SearchableOption[]>(() => {
    const options = [
      ...fields.store.map((f) => ({ value: f.key, label: f.label, group: 'Store' })),
      ...fields.people.map((f) => ({ value: f.key, label: f.label, group: 'People at the store' })),
    ];
    // A field the project no longer has stays readable, so it can be swapped for another.
    for (const c of initial?.conditions ?? []) {
      if (!byKey.has(c.field) && !options.some((o) => o.value === c.field)) options.push({ value: c.field, label: 'Removed field', group: 'Store' });
    }
    return options;
  }, [fields, byKey, initial]);

  const [name, setName] = useState(initial?.name ?? '');
  const [conditions, setConditions] = useState<DraftCondition[]>(() => (initial?.conditions.length
    ? initial.conditions.map((c) => ({
      field: c.field, op: c.op, values: [...(c.values ?? [])], text: (c.values ?? []).join(', '),
      number: typeof c.number === 'number' ? String(c.number) : '',
    }))
    : [{ ...EMPTY_CONDITION }]));
  const [mode, setMode] = useState<Mode>(initial?.visits.mode ?? 'per_month');
  const [months, setMonths] = useState<[string, string, string]>(() => (initial?.visits.mode === 'per_month'
    ? initial.visits.months.map(String) as [string, string, string]
    : ['1', '1', '1']));
  const [count, setCount] = useState(initial?.visits.mode === 'per_quarter' ? String(initial.visits.count) : '1');
  const [errors, setErrors] = useState<{ name?: string; conditions: Record<number, string>; visits?: string }>({ conditions: {} });

  const editCondition = (index: number, change: Partial<DraftCondition>) => {
    setConditions((list) => list.map((c, i) => (i === index ? { ...c, ...change } : c)));
    setErrors((e) => {
      if (!e.conditions[index]) return e;
      const next = { ...e.conditions };
      delete next[index];
      return { ...e, conditions: next };
    });
  };

  const pickField = (index: number, key: string) => {
    const info = byKey.get(key);
    editCondition(index, {
      field: key as PlanFieldKey,
      op: info?.ops[0] ?? '',
      values: [],
      text: '',
      number: START_NUMBER[key] ?? '',
    });
  };

  const removeCondition = (index: number) => {
    setConditions((list) => list.filter((_, i) => i !== index));
    setErrors((e) => ({ ...e, conditions: {} }));
  };

  // Picker or text box is decided by the field's own listed values only, never by what was typed.
  const listsValues = (c: DraftCondition): boolean => (c.field ? (byKey.get(c.field)?.values?.length ?? 0) > 0 : false);
  const valuesOf = (c: DraftCondition): string[] => (listsValues(c) ? c.values : typedValues(c.text));

  const conditionError = (c: DraftCondition): string | null => {
    if (!c.field || !c.op) return 'Pick a field.';
    if (VALUE_OPS.includes(c.op) && valuesOf(c).length === 0) return listsValues(c) ? 'Pick at least one value.' : 'Type at least one value.';
    if (NUMBER_OPS.includes(c.op)) {
      const range = RANGE[c.field];
      if (range) {
        const n = wholeNumber(c.number);
        if (n === null || n < range[0] || n > range[1]) return `Enter a whole number from ${range[0]} to ${range[1]}.`;
      } else if (c.number.trim() === '' || !Number.isFinite(Number(c.number))) {
        return 'Enter a number.';
      }
    }
    return null;
  };

  const done = () => {
    const found: typeof errors = { conditions: {} };
    if (name.trim() === '') found.name = 'Enter a name.';
    conditions.forEach((c, i) => {
      const error = conditionError(c);
      if (error) found.conditions[i] = error;
    });
    let visits: PlanVisits | null = null;
    if (mode === 'per_month') {
      const counts = months.map(wholeNumber);
      if (counts.some((n) => n === null || n > VISITS_MAX) || !counts.some((n) => (n ?? 0) > 0)) found.visits = MONTH_ERROR;
      else visits = { mode, months: counts as [number, number, number] };
    } else {
      const n = wholeNumber(count);
      if (n === null || n < 1 || n > VISITS_MAX) found.visits = QUARTER_ERROR;
      else visits = { mode, count: n };
    }
    setErrors(found);
    if (found.name || found.visits || Object.keys(found.conditions).length > 0 || !visits) return;

    const out: PlanCondition[] = conditions.map((c) => {
      const op = c.op as ConditionOp;
      const field = c.field as PlanFieldKey;
      if (VALUE_OPS.includes(op)) return { field, op, values: valuesOf(c) };
      if (NUMBER_OPS.includes(op)) return { field, op, number: Number(c.number) };
      return { field, op };
    });
    onDone({ name: name.trim(), conditions: out, visits });
  };

  const conditionRow = (c: DraftCondition, index: number) => {
    const info: PlanFieldInfo | undefined = c.field ? byKey.get(c.field) : undefined;
    const ops = info?.ops ?? (c.op ? [c.op] : []);
    const error = errors.conditions[index];
    const rowId = `${id}-c${index}`;
    const isValues = c.op !== '' && VALUE_OPS.includes(c.op);
    const isNumber = c.op !== '' && NUMBER_OPS.includes(c.op);
    const hasList = listsValues(c);
    // A picked value the field no longer lists stays visible, so it can be taken off.
    const valueList = hasList ? Array.from(new Set([...(info?.values ?? []), ...c.values])) : [];
    return (
      <li key={index} className="py-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-8 text-xs font-medium text-gray-500" aria-hidden={index === 0}>{index > 0 ? 'and' : ''}</span>
          <label htmlFor={`${rowId}-field`} className="sr-only">{`Field ${index + 1}`}</label>
          <div className="w-56">
            <SearchableSelect
              id={`${rowId}-field`}
              value={c.field}
              onChange={(v) => pickField(index, v)}
              options={fieldOptions}
              placeholder="Pick a field"
              allowClear={false}
              minPanelWidth={240}
            />
          </div>
          {ops.length > 1 ? (
            <select
              aria-label={`Comparison ${index + 1}`}
              value={c.op}
              onChange={(e) => editCondition(index, { op: e.target.value as ConditionOp })}
              className={`${FIELD} border-gray-300 bg-white py-2 text-gray-900`}
            >
              {ops.map((op) => <option key={op} value={op}>{OP_LABEL[op] ?? op}</option>)}
            </select>
          ) : c.op && c.op !== 'within_months' && c.op !== 'months_running' && c.op !== 'is_true' ? (
            <span className="text-sm text-gray-600">{OP_LABEL[c.op]}</span>
          ) : null}
          {isValues && (hasList ? (
            <>
              <label htmlFor={`${rowId}-values`} className="sr-only">{`Values ${index + 1}`}</label>
              <div className="min-w-[10rem] flex-1">
                <MultiSearchableSelect
                  id={`${rowId}-values`}
                  values={c.values}
                  onChange={(values) => editCondition(index, { values })}
                  options={valueList.map((v) => ({ value: v, label: valueLabel(c.field as PlanFieldKey, v) }))}
                  placeholder="Pick values"
                />
              </div>
            </>
          ) : (
            <input
              type="text"
              aria-label={`Values ${index + 1}`}
              placeholder="Type values, comma between"
              value={c.text}
              onChange={(e) => editCondition(index, { text: e.target.value })}
              onBlur={() => editCondition(index, { text: typedValues(c.text).join(', ') })}
              className={`min-w-[10rem] flex-1 ${FIELD} border-gray-300 py-2`}
            />
          ))}
          {isNumber && (
            <>
              <input
                type="number"
                step={RANGE[c.field] ? 1 : 'any'}
                min={RANGE[c.field]?.[0]}
                max={RANGE[c.field]?.[1]}
                inputMode={RANGE[c.field] ? 'numeric' : 'decimal'}
                aria-label={`Number ${index + 1}`}
                aria-invalid={Boolean(error)}
                value={c.number}
                onChange={(e) => editCondition(index, { number: e.target.value })}
                className={`w-20 ${FIELD} py-2 ${error ? 'border-red-400' : 'border-gray-300'}`}
              />
              {c.op === 'within_months' && <span className="text-sm text-gray-600">months</span>}
              {c.op === 'months_running' && <span className="text-sm text-gray-600">months running</span>}
            </>
          )}
          {conditions.length > 1 && (
            <button
              type="button"
              aria-label={`Remove condition ${index + 1}`}
              onClick={() => removeCondition(index)}
              className="ml-auto p-1 text-gray-400 hover:text-gray-700"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
        {error && <p role="alert" className={ERROR}>{error}</p>}
      </li>
    );
  };

  const monthBox = (i: 0 | 1 | 2) => (
    <label key={i} className="inline-flex items-center gap-1.5 text-sm text-gray-700">
      {`M${i + 1}`}
      <input
        type="number"
        min={0}
        max={VISITS_MAX}
        step={1}
        inputMode="numeric"
        aria-label={`Visits in M${i + 1}`}
        aria-invalid={Boolean(errors.visits) && mode === 'per_month'}
        value={months[i]}
        disabled={mode !== 'per_month'}
        onChange={(e) => {
          const value = e.target.value;
          setMonths((m) => m.map((v, j) => (j === i ? value : v)) as [string, string, string]);
          setErrors((er) => ({ ...er, visits: undefined }));
        }}
        className={`w-16 ${FIELD} ${errors.visits && mode === 'per_month' ? 'border-red-400' : 'border-gray-300'}`}
      />
    </label>
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={initial ? 'Edit priority' : 'Add priority'}
      size="lg"
      footer={(
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={done}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700"
          >
            {initial ? 'Done' : 'Add'}
          </button>
        </>
      )}
    >
      <div className="space-y-5">
        <div>
          <label htmlFor={`${id}-name`} className="text-sm font-semibold text-gray-900">Name</label>
          <input
            id={`${id}-name`}
            type="text"
            maxLength={PRIORITY_NAME_MAX}
            value={name}
            aria-invalid={Boolean(errors.name)}
            onChange={(e) => { setName(e.target.value); setErrors((er) => ({ ...er, name: undefined })); }}
            className={`mt-1 ${TEXT} ${errors.name ? 'border-red-400' : 'border-gray-300'}`}
          />
          {errors.name && <p role="alert" className={ERROR}>{errors.name}</p>}
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-900">When</h4>
          <ul className="mt-1">{conditions.map(conditionRow)}</ul>
          <button
            type="button"
            disabled={conditions.length >= MAX_CONDITIONS}
            onClick={() => setConditions((list) => [...list, { ...EMPTY_CONDITION }])}
            className={ADD_LINK}
          >
            + and
          </button>
        </div>

        <fieldset>
          <legend className="text-sm font-semibold text-gray-900">Visits</legend>
          <div className="mt-2 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex min-w-[11rem] items-center gap-2 text-sm text-gray-900">
                <input
                  type="radio"
                  name={`${id}-mode`}
                  checked={mode === 'per_month'}
                  onChange={() => { setMode('per_month'); setErrors((er) => ({ ...er, visits: undefined })); }}
                  className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Each month
              </label>
              {monthBox(0)}
              {monthBox(1)}
              {monthBox(2)}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex min-w-[11rem] items-center gap-2 text-sm text-gray-900">
                <input
                  type="radio"
                  name={`${id}-mode`}
                  checked={mode === 'per_quarter'}
                  onChange={() => { setMode('per_quarter'); setErrors((er) => ({ ...er, visits: undefined })); }}
                  className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Any time in the quarter
              </label>
              <input
                type="number"
                min={1}
                max={VISITS_MAX}
                step={1}
                inputMode="numeric"
                aria-label="Visits in the quarter"
                aria-invalid={Boolean(errors.visits) && mode === 'per_quarter'}
                value={count}
                disabled={mode !== 'per_quarter'}
                onChange={(e) => { setCount(e.target.value); setErrors((er) => ({ ...er, visits: undefined })); }}
                className={`w-16 ${FIELD} ${errors.visits && mode === 'per_quarter' ? 'border-red-400' : 'border-gray-300'}`}
              />
            </div>
          </div>
          {errors.visits && <p role="alert" className={ERROR}>{errors.visits}</p>}
        </fieldset>
      </div>
    </Modal>
  );
}
